import { PetrafiIcon } from '@repo/ui/components'
import { TenantStatusIcon, type TenantStatusLabels } from '@/components/TenantStatusIcon'
import { useAuthUser } from '@/hooks/queries/auth/useAuthUser'
import { useSwitchTenant } from '@/hooks/queries/auth/useSwitchTenant'
import { messageCodeCopy } from '@/lib/messageCode'
import { createHonoClient } from '@/stores/api'
import {
  getPendingTenantSelection,
  getSessionToken,
  pendingTenantSelectionAtom,
  setPendingTenantSelection,
  setSessionToken,
  setTenant,
} from '@/stores/session'
import { Button } from '@repo/ui/components'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { LoaderCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const unauthApi = createHonoClient(null)

export const Route = createFileRoute('/carriers')({
  beforeLoad: () => {
    if (!getSessionToken() && !getPendingTenantSelection()) {
      throw redirect({ to: '/login' })
    }
  },
  component: SelectCarrierPage,
})

function SelectCarrierPage() {
  const { t } = useTranslation('ui')
  const pending = useAtomValue(pendingTenantSelectionAtom)
  const hasSession = getSessionToken() !== null
  const { data: meData } = useAuthUser({ enabled: hasSession })
  const {
    switchTenant,
    isPending: isSwitching,
    error: switchError,
    reset: resetSwitch,
    pendingTenantId,
  } = useSwitchTenant()
  const navigate = useNavigate()
  const [selectingId, setSelectingId] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  const tenants = pending ? pending.tenants : (meData?.tenants ?? [])

  // Single-tenant users shouldn't see a one-option picker — send them straight
  // to their dashboard. Only fires for the logged-in (non-pending) path; the
  // verify-code multi-tenant flow always returns 2+ pending tenants.
  useEffect(() => {
    if (pending || !meData?.tenants) return
    if (meData.tenants.length !== 1) return
    const only = meData.tenants[0]!
    navigate({
      to: '/carriers/$slug/driver-onboarding/dashboard',
      params: { slug: only.slug },
      replace: true,
    })
  }, [pending, meData, navigate])

  const statusLabels: TenantStatusLabels = {
    action_needed: t('tenantSwitcher.status.actionNeeded'),
    waiting: t('tenantSwitcher.status.waiting'),
    success: t('tenantSwitcher.status.completed'),
    terminal: t('tenantSwitcher.status.closed'),
  }

  const selectFromPending = async (tenantId: string) => {
    if (!pending) return
    setSelectingId(tenantId)
    setLocalError(null)
    try {
      const res = await unauthApi['portal-auth'].driver['select-tenant'].$post({
        json: { tempToken: pending.tempToken, tenantId },
      })
      const data = await res.json()
      setSessionToken(data.sessionToken)
      setTenant(data.tenant)
      setPendingTenantSelection(null)
      window.location.assign(`/carriers/${data.tenant.slug}/driver-onboarding/dashboard`)
    } catch (err: unknown) {
      setLocalError(messageCodeCopy(t, err, 'login.error.codes', 'login.error.generic'))
      setSelectingId(null)
    }
  }

  const handleSelect = (tenantId: string) => {
    if (selectingId || isSwitching) return
    if (switchError) resetSwitch()
    if (pending) {
      void selectFromPending(tenantId)
    } else {
      switchTenant(tenantId)
    }
  }

  const handleBack = () => {
    setPendingTenantSelection(null)
    navigate({ to: '/login' })
  }

  const busy = isSwitching || selectingId !== null
  const errorMessage = localError ?? (switchError ? t('tenantSwitcher.switchError') : null)

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6">
      <div className="flex items-center gap-2">
        <PetrafiIcon className="size-10 text-primary" />
        <span className="text-2xl font-bold">{t('driverPortal')}</span>
      </div>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-bold">{t('login.tenantPicker.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('login.tenantPicker.subtitle')}</p>
        </div>

        {errorMessage && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {errorMessage}
          </div>
        )}

        <div className="grid gap-3">
          {tenants.map((tenant) => {
            const isLoading =
              selectingId === tenant.id || (isSwitching && pendingTenantId === tenant.id)
            return (
              <Button
                key={tenant.id}
                variant="outline"
                className="h-auto justify-start gap-2 px-4 py-3"
                onPress={() => handleSelect(tenant.id)}
                isDisabled={busy}
              >
                {isLoading && <LoaderCircleIcon className="size-3 animate-spin" />}
                <span className="font-medium">{tenant.name}</span>
                <span className="ml-auto flex items-center">
                  <TenantStatusIcon status={tenant.driverStatus} labels={statusLabels} />
                </span>
              </Button>
            )
          })}
        </div>

        {pending && (
          <Button variant="ghost" className="w-full" onPress={handleBack}>
            {t('login.tenantPicker.back')}
          </Button>
        )}
      </div>
    </div>
  )
}
